'use client';

import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { ButtonIcon } from '../ui/button-icon';
import { FOOTER_CONFIG } from '../../constants/footer-config';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

export function DesktopFooter({
  menus,
  generalLayout,
}: {
  menus: Menu[];
  generalLayout: GeneralLayout['data'];
}) {
  return (
    <div className="hidden h-full w-full flex-col items-start justify-between gap-6 sm:flex">
      <div className="grid w-full grid-cols-3 gap-6 xl:gap-8">
        {menus.map(menu => (
          <div key={menu.name} className="flex w-full flex-col items-start justify-start gap-4">
            <Heading title={menu.name} tag="h2" styleAs="h5" />
            {menu.fields.map(field => (
              <Link
                key={field.name}
                prefetch={false}
                href={getSafeHref(field.path ?? '')}
                className={cn('generic-hover', 'text-sm tracking-tight lg:text-base')}
              >
                {field.name}
              </Link>
            ))}
          </div>
        ))}
        <div className="flex w-full flex-col items-start justify-start gap-4">
          <Heading title={'Kontaktné údaje'} tag="h2" styleAs="h5" />
          <Paragraph size="lg">
            <strong className="font-semibold">Infolinka</strong> (8:00 - 16:30)
          </Paragraph>
          <Link
            prefetch={false}
            href={`tel:${generalLayout?.contact?.phone?.replace(/\s+/g, '')}`}
            className={cn(
              'generic-hover',
              'flex items-center justify-start gap-2 text-sm font-semibold text-primary lg:text-base',
            )}
          >
            <ButtonIcon alt="Zavolať" url="/images/icons/phone-primary.svg" />
            {generalLayout?.contact?.phone}
          </Link>
          <Link
            prefetch={false}
            href={`mailto:${generalLayout?.contact?.email}`}
            className={cn(
              'generic-hover',
              'flex min-w-0 max-w-full items-center justify-start gap-2 text-sm font-semibold text-primary lg:text-base',
            )}
          >
            <ButtonIcon alt="Email" url="/images/icons/email-primary.svg" />
            <span className="min-w-0 break-words [overflow-wrap:anywhere]">
              {generalLayout?.contact?.email}
            </span>
          </Link>
          <div
            className={cn(
              'flex items-start justify-start gap-2 text-sm font-semibold text-primary lg:text-base',
            )}
          >
            <ButtonIcon alt="Lokalita" url="/images/icons/location-primary.svg" />
            {generalLayout?.contact?.address} {generalLayout?.contact?.postcode}{' '}
            {generalLayout?.contact?.city}
          </div>
        </div>
      </div>
      <div className="flex w-full flex-wrap items-center justify-between gap-4">
        <div className="flex items-center justify-start gap-2">
          {FOOTER_CONFIG.payment_methods.map(method => (
            <Image
              key={method.name}
              src={method.icon}
              alt={method.name}
              width={54}
              height={28}
              className="shrink-0 object-contain"
            />
          ))}
          <Image
            src={'/images/icons/lock-password-foreground.svg'}
            alt="Záruka bezpečného nákupu"
            width={16}
            height={16}
            className="shrink-0"
          />
          <Paragraph size="base" color="muted-foreground">
            Záruka bezpečného nákupu
          </Paragraph>
        </div>
        <SocialIcons
          className={{
            button:
              'max-h-12 min-h-12 min-w-12 max-w-12 rounded-full border-none bg-white transition-all hover:bg-white/90',
            icon: 'fill-black text-black',
            iconLink: 'fill-black text-black',
          }}
        />
      </div>
    </div>
  );
}
